import { Dropdown, Menu, message } from 'antd'
import React, { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import { RootState } from '../../../../../app/store'
import { fetchRecommendKeywordsByInput } from '../../../../../actions/photo'
import type { MenuProps } from 'antd'
type ClickParam = Parameters<NonNullable<MenuProps['onClick']>>[0]
import CONFIG from '../../../../../constants/config'
import { DepartmentMemberV2 } from '../../../../../global'
import { setLocationRecommend } from '../../../../../slices/photosByLocationSlice'
import { useDebounceFn } from 'ahooks'
import { setUiFilter as setPhotoFilterSlice } from '../../../../../slices/photoFilterSlice'
import { FilterState } from '../../types'
import {
  calcPickedMemberNum,
  isTimeFilterActive
} from '../../utils/filterUtils'
import { PhotoFilterState } from '../../../../../slices/photoFilterSlice'
import SearchIcon from '../icons/SearchIcon'
import ai_suggestion_icon from '../../../../../images/input_ai_icon.png'
import user_icon from '../../../../../images/input_user_icon.png'
import { trackPicsPageClick } from '../../utils/sensorUtils'
import { useSearchHistory } from '../../../../../app/hooks/useSearchKeywordsHistory'
import { useWebSearchFilterHistory } from '../../../../../app/hooks/useWebSearchFilterHistory'
import { useWebSearchKeywordFilterHistory } from '../../../../../app/hooks/useWebSearchFilterHistory'

interface TagsInputDropdownForKeywordsProps {
  tags: string[]
  input: string
  onSubmitTag: (value: string[]) => void
  onSensorOfClickEvent: (clickItem: string, restProperties: any) => void
  onSubmit: (
    latestKeywords?: string[],
    overrideFilter?: Partial<FilterState>
  ) => void
  // NLP 搜索
  onNlpSubmit: (latestKeywords?: string[], options?: any) => void
  // 把文本写入输入框
  onInputChange: (text: string) => void
  suppressTriggerToken?: number
  inputFocused?: boolean
  aiSuggestion?: { text: string; structured?: any } | null
}

type RecommendWordType = {
  word: string
  rank: number
  headImage?: string
  userID?: string
  source: 'photo' | 'nickname' | 'location' | 'ai'
  sensorSource: string
  locationKey: string
  /** 时间+关键词 AI 推荐：用于 NLP 搜索的纯文本（无「」） */
  nlpQuery?: string
}

type DataRecommendWordsType = {
  msg: string
  status: number
  recommendWords: RecommendWordType[]
}

const HighLight = ({ word, input }: { word: string; input: string }) => {
  const characters = word.split('').map((char, c_index) => {
    if (input.includes(char)) {
      return (
        <span key={c_index} style={{ color: '#0093FF' }}>
          {char}
        </span>
      )
    }
    return <span key={c_index}>{char}</span>
  })
  return <span>{characters}</span>
}

const createMenu = (
  menuData: RecommendWordType[],
  input: string,
  onMenuClick: (param: ClickParam) => void
) => {
  return (
    <Menu onClick={onMenuClick}>
      {menuData.map((item, index) => {
        if (item.source === 'nickname') {
          return (
            <Menu.Item
              key={index}
              style={{ paddingLeft: 16 }}
              onMouseDown={(e) => {
                // 阻止输入框blur
                e.preventDefault()
              }}
            >
              <img
                src={user_icon}
                style={{ width: '18px', height: '18px', marginRight: 7 }}
              />
              <img
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  marginRight: '8px'
                }}
                src={item.headImage + CONFIG.ossImgProcessThumb}
                alt=""
              />
              <span>
                <HighLight word={item.word} input={input} />
              </span>
            </Menu.Item>
          )
        }
        return (
          <Menu.Item
            key={index}
            style={{ paddingLeft: 16 }}
            onMouseDown={(e) => {
              // 阻止输入框blur
              e.preventDefault()
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: 4,
                verticalAlign: 'middle'
              }}
            >
              <SearchIcon />
            </span>
            <HighLight word={item.word} input={input} />
            {item.source === 'ai' && (
              <img
                src={ai_suggestion_icon}
                style={{ width: '24px', height: '17px', marginLeft: 4 }}
              />
            )}
          </Menu.Item>
        )
      })}
    </Menu>
  )
}

// 字符串类型的放最前, 人员, 关键词
const getOrder = (item: RecommendWordType) => {
  if (item.source === 'ai') return 1
  if (item.source === 'nickname') return 2
  if (item.source === 'photo') return 3
  return 3
}

const isDefaultTimeAndMemberForSuggest = (
  filter: PhotoFilterState | undefined,
  totalMemberCount: number
): boolean => {
  if (!filter) return false
  if (isTimeFilterActive(filter as FilterState)) return false
  const picked = calcPickedMemberNum(filter as FilterState, totalMemberCount)
  return picked === totalMemberCount
}

/** 在时间与成员均为默认时，随机生成「今日 / 本月 / 过去90天」+ 关键词 的 AI 推荐项 */
const buildRandomTimeKeywordAiItem = (
  keyword: string
): RecommendWordType | null => {
  const kw = keyword.trim()
  if (!kw) return null

  const variant = Math.floor(Math.random() * 3)
  const now = dayjs()

  let displayLabel: string
  let nlpQuery: string

  if (variant === 0) {
    const d = now.format('M月D日')
    displayLabel = `${d}的「${kw}」`
    nlpQuery = `${d}的${kw}`
  } else if (variant === 1) {
    const start = now.clone().startOf('month')
    const end = now.clone()
    const range = `${start.format('M月D日')}-${end.format('M月D日')}`
    displayLabel = `${range}的「${kw}」`
    nlpQuery = `${range}的${kw}`
  } else {
    const end = now.clone().startOf('day')
    const start = now.clone().subtract(90, 'days').startOf('day')
    const rangeStr =
      start.year() === end.year()
        ? `${start.format('M月D日')}-${end.format('M月D日')}`
        : `${start.format('YYYY年M月D日')}-${end.format('M月D日')}`
    displayLabel = `${rangeStr}的「${kw}」`
    nlpQuery = `${rangeStr}的${kw}`
  }

  return {
    word: displayLabel,
    rank: 0,
    headImage: '',
    userID: '',
    source: 'ai',
    sensorSource: 'timeKeywordAi',
    locationKey: '',
    nlpQuery
  }
}

const TagsInputDropdownForKeywords: React.FC<
  TagsInputDropdownForKeywordsProps
> = (props) => {
  const {
    tags,
    input,
    onSubmitTag,
    onSensorOfClickEvent,
    onSubmit,
    onNlpSubmit,
    onInputChange,
    suppressTriggerToken,
    inputFocused = false,
    aiSuggestion = null
  } = props
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const groupID = useAppSelector((state) => state.group.groupId)
  const pickerUserCount = useAppSelector(
    (state: any) => state.organization?.pickerOrgUserCount ?? 0
  )
  const abTestConfig = useAppSelector((state: any) => state.main?.abTestConfig)
  const enableKeywordHistory = Boolean(
    abTestConfig?.webSearchHistory?.keywordHistory
  )

  const photoFilterState = useAppSelector(
    (state) => state.photoFilter?.uiFilter
  )

  const { history: typedKeywordHistory = [] } = useSearchHistory()
  const { history: filterHistory = [] } = useWebSearchFilterHistory()
  const { history: keywordFilterHistory = [] } =
    useWebSearchKeywordFilterHistory()

  const showKeywordHistoryModule =
    enableKeywordHistory &&
    inputFocused &&
    tags.length === 0 &&
    input.trim() === ''

  const mergedKeywordHistoryItems = useMemo(() => {
    const typedItems = typedKeywordHistory.map((item) => ({
      key: `typed_${item.word}`,
      text: item.word,
      createdAt: Number(item.createdAt) || 0,
      isNlp: item.isNlp,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const filterItems = filterHistory.map((item) => ({
      key: `filter_${item.id}`,
      text: item.text,
      createdAt: Number(item.createdAt) || 0,
      isNlp: true as const,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const keywordFilterItems = keywordFilterHistory.map((item) => ({
      key: `keyword_filter_${item.id}`,
      text: item.text,
      createdAt: Number(item.createdAt) || 0,
      isNlp: true as const,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const sorted = [...typedItems, ...filterItems, ...keywordFilterItems].sort(
      (a, b) => b.createdAt - a.createdAt
    )

    // 与 KeywordsSearch 一致，只保留时间最新的一条
    const seenText = new Set<string>()
    return sorted.filter((row) => {
      if (seenText.has(row.text)) return false
      seenText.add(row.text)
      return true
    })
  }, [typedKeywordHistory, filterHistory, keywordFilterHistory])

  const [menuData, setMenuData] = React.useState<RecommendWordType[]>([])
  const currentInputRef = React.useRef<string>('')
  // 记录最近一次点击的推荐词，避免点击后立刻再次触发推荐
  const lastSelectedWordRef = React.useRef<string | null>(null)

  const sortedMenuData = useMemo(() => {
    return menuData.sort((a, b) => {
      return getOrder(a) - getOrder(b)
    })
  }, [menuData])

  const lastAiGuideExposureKeyRef = React.useRef<string | null>(null)

  useEffect(() => {
    if (!inputFocused) {
      lastAiGuideExposureKeyRef.current = null
      return
    }
    if (showKeywordHistoryModule) return
    if (!sortedMenuData.length) return

    const aiGuide = sortedMenuData.find(
      (item) =>
        item.source === 'ai' &&
        (item.sensorSource === 'timeKeywordAi' || item.sensorSource === 'ai')
    )
    if (!aiGuide) return

    const key = `${input.trim()}|${aiGuide.word}|${aiGuide.sensorSource}`
    if (lastAiGuideExposureKeyRef.current === key) return
    lastAiGuideExposureKeyRef.current = key

    onSensorOfClickEvent('AI搜索引导曝光', { bool4: true })
  }, [
    sortedMenuData,
    input,
    inputFocused,
    showKeywordHistoryModule,
    onSensorOfClickEvent
  ])

  const handleFetchRecommendTags = () => {
    if (!input) return
    dispatch(
      fetchRecommendKeywordsByInput({
        groupID,
        query: input
      })
    ).then((data: DataRecommendWordsType) => {
      // 检查当前input是否与发起请求时的input一致
      if (
        data.status === 0 &&
        data.recommendWords &&
        currentInputRef.current === input
      ) {
        const state = store.getState()
        const uiFilter = state.photoFilter?.uiFilter
        const kwTrim = input.trim()

        const finalWords = [...data.recommendWords]

        // 时间、人员均为默认 + 有输入：随机插入「今日/本月/过去90天」+ 关键词（与结构化 AI 引导互斥）
        const shouldShowTimeKeywordAi =
          kwTrim.length > 0 &&
          !aiSuggestion &&
          isDefaultTimeAndMemberForSuggest(uiFilter, pickerUserCount)

        if (shouldShowTimeKeywordAi) {
          const timeKwItem = buildRandomTimeKeywordAiItem(kwTrim)
          if (timeKwItem) {
            finalWords.unshift(timeKwItem)
          }
        }

        // 如果存在 aiSuggestion，并且当前没有已存在的 tag（tags 为空），则把它插入到推荐结果
        if (
          aiSuggestion &&
          (!tags || tags.length === 0) &&
          input &&
          input.trim() !== ''
        ) {
          const aiItem: RecommendWordType = {
            word: `“${aiSuggestion.text}”`,
            rank: 0,
            headImage: '',
            userID: '',
            source: 'ai',
            sensorSource: 'ai',
            locationKey: ''
          }
          finalWords.push(aiItem)
        }
        setMenuData(finalWords)
      }
    })
  }

  const { run: runFetchRecommendTags, cancel: cancelFetchRecommendTags } =
    useDebounceFn(handleFetchRecommendTags, {
      wait: 200
    })
  const suppressTokenRef = React.useRef<number | null>(null)

  const updateDepartmentUsersFilter = (user: DepartmentMemberV2) => {
    if (photoFilterState) {
      dispatch(
        setPhotoFilterSlice({
          departmentUsers: [user],
          departments: []
        })
      )
    }

    return Promise.resolve()
  }

  const onMenuClick = (e: any) => {
    const menuData = sortedMenuData[e.key]
    // 时间 + 关键词 AI 推荐（随机今日/本月/过去90天）
    if (menuData && menuData.sensorSource === 'timeKeywordAi') {
      trackPicsPageClick({
        clickItem: 'Ai搜索推荐点击',
        bool4: true
      })
      const q = menuData.nlpQuery || menuData.word
      lastSelectedWordRef.current = menuData.word
      onInputChange(q)
      onNlpSubmit([q], { preserveInput: true })
      onSensorOfClickEvent('timeKeywordAiSuggestionClick', {
        suggestion: q,
        displayWord: menuData.word
      })
      setMenuData([])
      return
    }
    // AI引导点击逻辑
    if (menuData && menuData.sensorSource === 'ai') {
      trackPicsPageClick({
        clickItem: 'Ai搜索推荐点击',
        bool4: true
      })
      handleAiClick()
      return
    }
    // 记录本次选择的词，用于阻止立即重复触发推荐
    lastSelectedWordRef.current = menuData.word
    if (menuData.source === 'photo') {
      // 关键词搜索场景：只保留当前选中的推荐词
      onSubmitTag([menuData.word])
      setMenuData([])
    } else if (menuData.source === 'location') {
      if (tags.length === 0) {
        // 只有设置第一个tag的时候，才能设置为地点关键词
        dispatch(
          setLocationRecommend({
            word: menuData.word,
            key: menuData.locationKey
          })
        )
      }
      // 关键词搜索场景：只保留当前选中的推荐地点
      onSubmitTag([menuData.word])
      setMenuData([])
    } else {
      // 选择人员，加入到已筛选的部门、成员中
      onSubmitTag([])
      setMenuData([])
      const departmentUser: DepartmentMemberV2 = {
        userID: menuData.userID || '',
        name: menuData.word,
        avatar: menuData.headImage || '',
        id: menuData.userID || '',
        quit: false,
        role: 0
      }
      updateDepartmentUsersFilter(departmentUser).then(() => {
        message.info(`已筛选"${menuData.word}的照片"`)
        onSubmit([], {
          departmentUsers: [departmentUser],
          departments: []
        })
      })
    }
    onSensorOfClickEvent('vagueRecommendWord', {
      clickRecommendWordComment: menuData.word,
      recommendedWordPosition: e.key,
      recommendedWordSource: menuData.source,
      bool2: menuData.source === 'nickname',
      bool4: false,
      infoJson: JSON.stringify(
        sortedMenuData.map((item) => ({
          name: item.word,
          type: item.source,
          sensorSource: item.sensorSource
        }))
      )
    })
  }

  const handleAiClick = () => {
    if (!aiSuggestion) return
    // 把 AI 引导放到输入框（不转 tag）
    onInputChange(aiSuggestion.text)
    // 发起 NLP 搜索并保留输入
    onNlpSubmit([aiSuggestion.text], { preserveInput: true })
    onSensorOfClickEvent('aiSuggestionClick', {
      suggestion: aiSuggestion.text
    })
    setMenuData([])
  }

  useEffect(() => {
    currentInputRef.current = input
    if (!input) {
      cancelFetchRecommendTags()
      setMenuData([])
      // 清空输入时，允许后续重新触发推荐
      lastSelectedWordRef.current = null
      return
    }
    // 如果输入内容与刚刚选择的推荐词完全一致，则不再触发推荐，避免“点击一次又弹一次”
    if (lastSelectedWordRef.current && input === lastSelectedWordRef.current) {
      cancelFetchRecommendTags()
      setMenuData([])
      return
    }
    runFetchRecommendTags()
    return () => {
      cancelFetchRecommendTags()
    }
  }, [input, runFetchRecommendTags, cancelFetchRecommendTags])

  useEffect(() => {
    if (suppressTriggerToken === undefined || suppressTriggerToken === null) {
      return
    }
    if (suppressTokenRef.current === suppressTriggerToken) {
      return
    }
    suppressTokenRef.current = suppressTriggerToken
    lastSelectedWordRef.current = input
    cancelFetchRecommendTags()
    setMenuData([])
  }, [suppressTriggerToken, cancelFetchRecommendTags, input])

  useEffect(() => {
    if (inputFocused) return
    cancelFetchRecommendTags()
    setMenuData([])
  }, [inputFocused, cancelFetchRecommendTags])

  // 历史记录下拉框
  if (showKeywordHistoryModule) {
    if (!mergedKeywordHistoryItems.length) {
      return null
    }

    const maxHeightPx = 320 // 约等于10条高度，超过后滚动

    return (
      <Dropdown
        popupRender={() => (
          <Menu
            style={{
              maxHeight: maxHeightPx,
              overflowY: 'auto'
            }}
          >
            {mergedKeywordHistoryItems.map((item) => (
              <Menu.Item
                key={item.key}
                style={{ paddingLeft: 16 }}
                onMouseDown={(e) => {
                  // 阻止输入框 blur，保证点击生效
                  e.preventDefault()
                }}
                onClick={() => {
                  onSensorOfClickEvent('recommendSearch', {
                    clickSearchContent: {
                      word: item.text,
                      type: 'history',
                      isNlp: item.isNlp,
                      isSessionGenerated: item.isSessionGenerated === true
                    },
                    bool1: true,
                    bool4: item.isSessionGenerated === true
                  })
                  onNlpSubmit([item.text])
                }}
              >
                <span
                  style={{
                    display: 'block',
                    maxWidth: 340,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.text}
                </span>
              </Menu.Item>
            ))}
          </Menu>
        )}
        open
        classNames={{ root: 'tags-input-dropdown' }}
        styles={{ root: { left: 300 } }}
        getPopupContainer={() =>
          document.getElementsByClassName('keywords-wrapper')[0] as HTMLElement
        }
      >
        <span className="tag-dropdown-bind-node"></span>
      </Dropdown>
    )
  }

  if (!menuData.length) {
    return null
  }
  return (
    <Dropdown
      popupRender={() => createMenu(sortedMenuData, input, onMenuClick)}
      open
      classNames={{ root: 'tags-input-dropdown' }}
      styles={{ root: { left: 300 } }}
      getPopupContainer={() =>
        document.getElementsByClassName('keywords-wrapper')[0] as HTMLElement
      }
    >
      <span className="tag-dropdown-bind-node"></span>
    </Dropdown>
  )
}

export default TagsInputDropdownForKeywords
